import { useCallback, useState, useEffect, useRef, useMemo, Fragment, useId } from 'react';

import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeftSVG } from 'components/iconsSVG/chevronLeft';

import { getDomainValue } from 'utils/domain';

import emblaStyles from 'components/productPurchaseCol/style.module.css';

import type { EmblaOptionsType } from 'embla-carousel';
import { IMAGE_SIZE_MAPPER } from 'utils/constants/ux';
import type { TGridProduct } from 'types/product-list';

/**
 * EmblaCarousel component with customizable navigation
 *
 * @example
 * // Using default navigation buttons
 * <EmblaCarousel imageSources={sources} template="square" />
 *
 * @example
 * // Using custom navigation buttons
 * <EmblaCarousel
 *   imageSources={sources}
 *   template="square"
 *   navigation={({ next, previous }) => (
 *     <>
 *       <button onClick={previous}>Previous</button>
 *       <button onClick={next}>Next</button>
 *     </>
 *   )}
 * />
 */
type EmblaCarouselProps = {
  config?: EmblaOptionsType;
  imageSources?: TGridProduct['imageSources'];
  template: string;
  style?: React.CSSProperties;
  customRender?: React.ReactNode;
  gradient?: React.ReactNode;
  buttons?: boolean;
  startAnimation?: boolean;
  navigation?: ({ next, previous }: { next: (e?: TODO) => void; previous: (e?: TODO) => void }) => React.ReactNode;
};

export function EmblaCarousel({
  config = { loop: false },
  imageSources,
  template,
  style = { '--slide-height': '3rem', '--slide-gap': '0.2rem' },
  customRender,
  gradient,
  buttons = true,
  startAnimation = false,
  navigation,
}: EmblaCarouselProps) {
  const [emblaRef, emblaAPI] = useEmblaCarousel({ ...config, duration: startAnimation ? 60 : 25 });
  const [previousButtonEnabled, setPreviousButtonEnabled] = useState(false);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef(null);
  const id = useId();
  const scrollPrevious = useCallback(() => emblaAPI && emblaAPI.scrollPrev(), [emblaAPI]);
  const scrollNext = useCallback(() => emblaAPI && emblaAPI.scrollNext(), [emblaAPI]);
  const scrollProgress = useCallback(() => emblaAPI && emblaAPI.scrollProgress(), [emblaAPI]);

  const productImages = useMemo(() => {
    if (customRender || !imageSources) return null;
    return imageSources.sources.reduce(
      (acc, source, index) => {
        const type = source.type as keyof typeof IMAGE_SIZE_MAPPER;
        source.links.forEach((link, linkIndex) => {
          if (!acc[linkIndex]) {
            if (link.video) {
              acc[linkIndex] = {
                altText: link.altText,
                video: link.video,
                [type]: link.url,
                src: link.url,
                sources: link.sources,
                poster: link.poster,
              };
              return acc;
            } else {
              acc[linkIndex] = {
                altText: link.altText,
                video: link.video,
              };
              acc[linkIndex][type] = link.url;
            }
          } else {
            acc[linkIndex][type] = link.url;
          }
        });
        return acc;
      },
      [] as { md?: string; full?: string; altText: string; video: boolean; src?: string; sources?: string; poster?: string }[],
    );
  }, [customRender, imageSources]);

  const numberOfImages = useMemo(() => {
    return productImages?.length || 0;
  }, [productImages]);

  const handleClickButton = (event: React.MouseEvent<HTMLAnchorElement | SVGSVGElement>, buttonType: 'next' | 'previous') => {
    event.preventDefault();
    event.stopPropagation();

    const scrollFunction = buttonType === 'next' ? scrollNext : scrollPrevious;

    scrollFunction();
  };

  // Accept a standard React.MouseEvent for button elements.
  const handleNext = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      scrollNext();
    },
    [scrollNext],
  );

  const handlePrevious = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      scrollPrevious();
    },
    [scrollPrevious],
  );

  const onSelect = useCallback(() => {
    if (!emblaAPI) return;
    setPreviousButtonEnabled(emblaAPI.canScrollPrev());
    setNextButtonEnabled(emblaAPI.canScrollNext());
  }, [emblaAPI]);

  const onSettle = useCallback(() => {
    const currentScrollProgress = scrollProgress();

    if (currentScrollProgress && currentScrollProgress > 0) {
      const currentScrollProgressPercentage = currentScrollProgress * 100; // 29
      const imagesPerScroll = 100 / numberOfImages; // 33.33
      const currentImage = Math.floor(currentScrollProgressPercentage / imagesPerScroll); // 0

      if (currentImage >= 0 && currentImage < numberOfImages && currentImage !== currentImageIndex) {
        setCurrentImageIndex(currentImage);
      }
    }
  }, [scrollProgress, numberOfImages, currentImageIndex]);

  useEffect(() => {
    if (!emblaAPI) return;
    onSelect();

    emblaAPI.on('select', onSelect);
    emblaAPI.on('scroll', onSettle);
  }, [emblaAPI, onSelect, onSettle]);

  useEffect(() => {
    if (!emblaAPI) return;

    if (startAnimation) {
      const runAnimation = async (): Promise<void> => {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        emblaAPI.scrollTo(1);
        await new Promise((resolve) => setTimeout(resolve, 750));
        emblaAPI.scrollTo(0);
        // await new Promise((resolve) => setTimeout(resolve, 1000));
        // emblaAPI.reInit({ ...config, duration: 25 });
      };

      runAnimation().catch(console.error);
    }
  }, [emblaAPI, startAnimation]);

  useEffect(() => {
    if (!emblaAPI) return;
    emblaAPI.scrollTo(0);
  }, [emblaAPI, imageSources]);

  const scrollIndicatorStyles = useMemo(
    () =>
      getDomainValue({
        camper: { width: `${(1 / numberOfImages) * 100}%`, left: `${(currentImageIndex / numberOfImages) * 100}%` },
        nnormal: {},
        camperlab: { width: `${(1 / numberOfImages) * 100}%` },
      }),
    [numberOfImages, currentImageIndex],
  );

  const scrollIndicator = useMemo(() => {
    return getDomainValue({
      camper: (
        <div className="absolute bottom-0 h-[2px] w-full">
          <div className="absolute h-[2px] bg-[#444]" style={scrollIndicatorStyles} />
        </div>
      ),
      nnormal: null,
      camperlab: (
        <div className="absolute bottom-0 h-[2px] w-[15%]">
          {numberOfImages > 1 ?
            Array.from({ length: numberOfImages }).map((_, index) => (
              <div
                className="absolute h-[2px]"
                style={{
                  ...scrollIndicatorStyles,
                  backgroundColor: index === currentImageIndex ? '#444' : '#ccc',
                  opacity: index === currentImageIndex ? 1 : 0.8,
                  left: `${(index / numberOfImages) * 100 + (index > 0 ? 6 * index : 0)}%`,
                }}
                key={index}
              />
            ))
          : null}
        </div>
      ),
    });
  }, [scrollIndicatorStyles]);

  return (
    <>
      {nextButtonEnabled && gradient ? gradient : null}
      <div className="relative overflow-hidden" data-square={template === 'square'} style={style} ref={emblaRef}>
        {customRender ?
          customRender
        : <div
            className={`flex h-full min-w-[50vw] lab:min-w-[calc(50vw-1.25rem)] md:min-w-[33vw] lab:md:min-w-[calc(33vw-1.25rem)] lg:min-w-[25vw] lab:lg:min-w-[calc(25vw-1.25rem)] ${template === 'square' ? 'aspect-square' : 'aspect-[1/1.5]'}`}
            ref={carouselRef}
          >
            {productImages?.map((imageData, index) => {
              if (imageData.video) {
                return (
                  <div
                    className={`relative w-full flex-shrink-0 select-none ${template === 'square' ? 'grid aspect-square grid-cols-1 grid-rows-1 items-end' : ''}`}
                    key={`${id}-${index}-image`}
                  >
                    <video
                      autoPlay
                      draggable={false}
                      loop
                      title={`Video of ${imageData.altText}`}
                      muted
                      playsInline
                      poster={imageData.poster}
                      inert
                      className="user-select-none pointer-events-none select-none"
                      dangerouslySetInnerHTML={{ __html: imageData.sources ?? '' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    <div
                      className="absolute inset-0 z-10 touch-none select-none"
                      onMouseDown={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </div>
                );
              }
              const imageEnd =
                typeof imageData.md === 'string' ?
                  imageData.md.match(/\.(jpg|png)$/) ?
                    imageData.md.match(/\.(jpg|png)$/)![0]
                  : null
                : null;
              if (!imageEnd) return null;
              return (
                <div className="w-full flex-shrink-0" key={`${id}-${index}-image`}>
                  <picture>
                    <source type="image/avif" srcSet={imageData.md?.replace(imageEnd, '.avif')} media={IMAGE_SIZE_MAPPER.md} />
                    <source type="image/avif" srcSet={imageData.full?.replace(imageEnd, '.avif')} media={IMAGE_SIZE_MAPPER.full} />
                    <source type="image/webp" srcSet={imageData.md?.replace(imageEnd, '.webp')} media={IMAGE_SIZE_MAPPER.md} />
                    <source type="image/webp" srcSet={imageData.full?.replace(imageEnd, '.webp')} media={IMAGE_SIZE_MAPPER.full} />
                    {imageEnd === '.png' ?
                      <>
                        <source type="image/png" srcSet={imageData.md} media={IMAGE_SIZE_MAPPER.md} />
                        <source type="image/png" srcSet={imageData.full} media={IMAGE_SIZE_MAPPER.full} />
                      </>
                    : null}
                    {imageEnd === '.jpg' ?
                      <>
                        <source type="image/jpg" srcSet={imageData.md} media={IMAGE_SIZE_MAPPER.md} />
                        <source type="image/jpg" srcSet={imageData.full} media={IMAGE_SIZE_MAPPER.full} />
                      </>
                    : null}
                    <img
                      loading={index < 2 ? 'eager' : 'lazy'}
                      src={imageData.md}
                      alt={imageData.altText}
                      className="h-full w-full object-cover"
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  </picture>
                </div>
              );
            })}
          </div>
        }
        {navigation ? navigation({ next: handleNext, previous: handlePrevious }) : null}
        {buttons ?
          <>
            {previousButtonEnabled && (
              <ChevronLeftSVG
                onClick={(e: React.MouseEvent<SVGSVGElement>) => handleClickButton(e, 'previous')}
                className={`${emblaStyles.emblaButton} absolute left-[0.1rem] hidden cursor-pointer bg-[#fff] xl:flex`}
              />
            )}
            {nextButtonEnabled && (
              <ChevronLeftSVG
                onClick={(e: React.MouseEvent<SVGSVGElement>) => handleClickButton(e, 'next')}
                className={`${emblaStyles.emblaButton} absolute right-[0.75rem] hidden rotate-180 cursor-pointer bg-[#fff] xl:flex`}
              />
            )}
          </>
        : scrollIndicator}
      </div>
    </>
  );
}
